// Etiquetas en español para los códigos del contrato.
import type {
  AreaMargen,
  Clasificacion,
  DimensionRiesgo,
  NivelRiesgo,
  Semaforo,
  EstadoCaso,
  MotivoNoOferta,
  Prioridad,
  Rol,
  TipoCaso,
  TipoCatalogo,
  TipoExcepcion,
} from '../api/tipos';

export const ETIQUETA_ESTADO: Record<EstadoCaso, string> = {
  RECIBIDO: 'Recibido',
  EN_ANALISIS: 'En análisis',
  ESPERANDO_CLIENTE: 'Esperando cliente',
  COSTEO_RIESGO: 'Costeo y riesgo',
  ESPERANDO_COSTEO: 'Esperando costeo',
  CASO_NEGOCIO: 'Caso de negocio',
  PROPUESTA_ASEGURAMIENTO: 'Propuesta y aseguramiento',
  POR_APROBAR: 'Por aprobar',
  APROBADO: 'Aprobado',
  ENVIADO: 'Enviado',
  SEGUIMIENTO: 'Seguimiento',
  GANADO: 'Ganado',
  PERDIDO: 'Perdido',
  DESISTIDO: 'Desistido',
  CERRADO_SIN_OFERTA: 'Cerrado sin oferta',
  EXCEPCION: 'Excepción',
};

export interface GrupoEstados {
  clave: string;
  nombre: string;
  estados: EstadoCaso[];
}

export const GRUPOS_ESTADO: GrupoEstados[] = [
  { clave: 'entrada', nombre: 'Entrada', estados: ['RECIBIDO', 'EN_ANALISIS', 'ESPERANDO_CLIENTE'] },
  {
    clave: 'trabajo',
    nombre: 'Trabajo',
    estados: ['COSTEO_RIESGO', 'ESPERANDO_COSTEO', 'CASO_NEGOCIO', 'PROPUESTA_ASEGURAMIENTO'],
  },
  { clave: 'decision', nombre: 'Decisión', estados: ['POR_APROBAR', 'APROBADO'] },
  { clave: 'seguimiento', nombre: 'Seguimiento', estados: ['ENVIADO', 'SEGUIMIENTO'] },
  { clave: 'cerrados', nombre: 'Cerrados', estados: ['GANADO', 'PERDIDO', 'DESISTIDO', 'CERRADO_SIN_OFERTA'] },
  { clave: 'excepcion', nombre: 'Excepción', estados: ['EXCEPCION'] },
];

export const ESTADOS_CERRADOS: EstadoCaso[] = ['GANADO', 'PERDIDO', 'DESISTIDO', 'CERRADO_SIN_OFERTA'];
export const TODOS_LOS_ESTADOS = GRUPOS_ESTADO.flatMap((g) => g.estados);

export function grupoDeEstado(e: EstadoCaso): GrupoEstados {
  return GRUPOS_ESTADO.find((g) => g.estados.includes(e)) ?? GRUPOS_ESTADO[0];
}

/** Clase visual del estado (color del indicador). */
export function claseEstado(estado: EstadoCaso): string {
  if (estado === 'GANADO') return 'ganado';
  if (estado === 'PERDIDO' || estado === 'DESISTIDO') return 'perdido';
  if (estado === 'CERRADO_SIN_OFERTA') return 'cerrados';
  return grupoDeEstado(estado).clave;
}

export const PRIORIDADES: Prioridad[] = ['alta', 'media', 'baja'];

export const ETIQUETA_TIPO: Record<TipoCaso, string> = {
  por_clasificar: 'Por clasificar',
  suministro: 'Suministro',
  servicio: 'Servicio',
  proyecto: 'Proyecto',
  licitacion: 'Licitación',
};

export const ETIQUETA_PRIORIDAD: Record<Prioridad, string> = { alta: 'Alta', media: 'Media', baja: 'Baja' };

export const ETIQUETA_MOTIVO: Record<MotivoNoOferta, string> = {
  no_rentable: 'No rentable bajo el piso',
  no_cumple_tecnicamente: 'No cumple técnicamente',
  riesgo_contractual_regulatorio: 'Riesgo contractual o regulatorio',
  cartera_riesgo_financiero: 'Cartera o riesgo financiero del cliente',
  sin_capacidad_plazo: 'Sin capacidad para el plazo',
  sin_disponibilidad_proveedor: 'Sin disponibilidad del proveedor',
  fuera_del_core: 'Fuera del core de UPK',
  fuera_de_alcance: 'Fuera del alcance de Preventa Colombia',
  decision_comercial: 'Decisión comercial',
};
export const MOTIVOS_NO_OFERTA = Object.keys(ETIQUETA_MOTIVO) as MotivoNoOferta[];

export const ETIQUETA_DIMENSION: Record<DimensionRiesgo, string> = {
  tecnico: 'Técnico',
  contractual: 'Contractual',
  financiero: 'Financiero',
  cartera: 'Cartera',
  ejecucion: 'Ejecución',
  regulatorio: 'Regulatorio',
};
export const DIMENSIONES: DimensionRiesgo[] = ['tecnico', 'contractual', 'financiero', 'cartera', 'ejecucion', 'regulatorio'];

export const ETIQUETA_CALIDAD: Record<string, string> = {
  requisitos: 'Requisitos',
  costos_vigentes: 'Costos vigentes (evidencia A/B)',
  stock: 'Stock',
  cliente: 'Cliente',
  condiciones_comerciales: 'Condiciones comerciales',
  documentacion: 'Documentación',
};

export const ETIQUETA_AREA: Record<AreaMargen, string> = {
  compras: 'Compras',
  operaciones: 'Operaciones',
  finanzas: 'Finanzas',
  cartera: 'Cartera',
};

/** Campos obligatorios por área (RULE-102). */
export const CAMPOS_AREA: Record<AreaMargen, { clave: string; etiqueta: string; tipo: 'dinero' | 'fecha' }[]> = {
  compras: [
    { clave: 'producto', etiqueta: 'Producto', tipo: 'dinero' },
    { clave: 'proveedores', etiqueta: 'Proveedores y terceros', tipo: 'dinero' },
  ],
  operaciones: [
    { clave: 'mano_obra', etiqueta: 'Mano de obra', tipo: 'dinero' },
    { clave: 'logistica', etiqueta: 'Logística', tipo: 'dinero' },
    { clave: 'reprocesos', etiqueta: 'Reprocesos', tipo: 'dinero' },
    { clave: 'consumos', etiqueta: 'Consumos', tipo: 'dinero' },
  ],
  finanzas: [
    { clave: 'impuestos_no_recuperables', etiqueta: 'Impuestos no recuperables', tipo: 'dinero' },
    { clave: 'polizas', etiqueta: 'Pólizas', tipo: 'dinero' },
    { clave: 'gastos_financieros', etiqueta: 'Gastos financieros', tipo: 'dinero' },
  ],
  cartera: [
    { clave: 'venta_facturada', etiqueta: 'Venta facturada', tipo: 'dinero' },
    { clave: 'fecha_recaudo', etiqueta: 'Fecha de recaudo', tipo: 'fecha' },
  ],
};
export const AREAS: AreaMargen[] = ['compras', 'operaciones', 'finanzas', 'cartera'];

export const ETIQUETA_EXCEPCION: Record<TipoExcepcion, string> = {
  fallo_tecnico: 'Fallo técnico',
  intervencion_humana: 'Intervención humana',
  inconsistencia_datos: 'Inconsistencia de datos',
  integracion_no_disponible: 'Integración no disponible',
  revision_seguridad: 'Revisión de seguridad',
  desviacion_aprobacion: 'Desviación de aprobación',
};

export const ETIQUETA_CLASIFICACION: Record<Clasificacion, string> = {
  publico: 'Público',
  interno: 'Interno',
  confidencial: 'Confidencial',
  confidencial_financiero: 'Confidencial financiero',
  datos_personales: 'Datos personales',
};

export const ETIQUETA_CATALOGO: Record<TipoCatalogo, string> = {
  reglas: 'Libro de reglas',
  parametros: 'Parámetros',
  maestro_corporativo: 'Maestro corporativo',
  maestro_costos: 'Maestro de costos',
  politicas_cliente: 'Políticas por cliente',
};

export const DESCRIPCION_CATALOGO: Record<TipoCatalogo, string> = {
  reglas: 'Reglas ejecutables por familia, con disparador, condición y excepción.',
  parametros: 'Valores numéricos que leen las reglas y el motor financiero.',
  maestro_corporativo: 'Datos legales de la sociedad, firmas y alcance.',
  maestro_costos: 'Costos internos con vigencia (tarifas, factores).',
  politicas_cliente: 'Condiciones particulares de cada cliente.',
};

export const ETIQUETA_ROL: Record<Rol, string> = {
  'Preventa.Responsable': 'Responsable de preventa',
  'Preventa.Suplente': 'Suplente de preventa',
  Gerencia: 'Gerencia',
  Comercial: 'Comercial',
  Juridica: 'Jurídica',
  Compras: 'Compras',
  Operaciones: 'Operaciones',
  Finanzas: 'Finanzas',
  Cartera: 'Cartera',
  Tecnico: 'Técnico',
  Lector: 'Lector',
  'Personas.GestionHumana': 'Gestión Humana',
  'Personas.Colaborador': 'Colaborador',
};

export const ETIQUETA_DOMINIO: Record<string, string> = {
  sistema: 'Sistema',
  entrada: 'Entrada',
  ingenieria: 'Ingeniería',
  comercial: 'Comercial',
  propuesta: 'Propuesta',
  aseguramiento: 'Aseguramiento',
  persona: 'Persona',
};

export const ETIQUETA_DECISION: Record<string, string> = {
  aprobar: 'Aprobada',
  ajustes: 'Ajustes solicitados',
  rechazar: 'Rechazada',
};

export const semaforoDeNivel = (n?: NivelRiesgo | null): Semaforo =>
  n === 'alto' ? 'ROJO' : n === 'medio' ? 'AMARILLO' : 'VERDE';

// ------------------------------------------------------------------ plataforma de preventa v1.2 (§9)
export const ETIQUETA_CALIFICACION: Record<string, { titulo: string; ayuda: string }> = {
  metricas: { titulo: 'Métricas', ayuda: 'Indicador con el que el cliente medirá el éxito.' },
  comprador_economico: { titulo: 'Comprador económico', ayuda: 'Quién aprueba la inversión.' },
  criterios_decision: { titulo: 'Criterios de decisión', ayuda: 'Qué pesa más en la evaluación de ofertas.' },
  proceso_decision: { titulo: 'Proceso de decisión', ayuda: 'Pasos y aprobaciones hasta la orden de compra.' },
  dolor: { titulo: 'Dolor identificado', ayuda: 'Problema que hoy cuesta dinero, tiempo o riesgo.' },
  campeon: { titulo: 'Campeón', ayuda: 'Quién impulsa el proyecto dentro del cliente.' },
  competencia: { titulo: 'Competencia', ayuda: 'Alternativas o proveedores que se evalúan.' },
  presupuesto: { titulo: 'Presupuesto', ayuda: 'Monto aprobado o disponible.' },
  fecha_decision: { titulo: 'Fecha de decisión', ayuda: 'Para cuándo decide el cliente.' },
};

export const ETIQUETA_GANANCIA: Record<string, string> = {
  no_evaluada: 'No evaluada',
  en_curso: 'En curso',
  ganada: 'Ganada',
  en_riesgo: 'En riesgo',
  perdida: 'Perdida',
};

export const ETIQUETA_TIPO_EVALUACION: Record<string, string> = {
  demo: 'Demo',
  poc: 'Prueba de concepto',
  visita_tecnica: 'Visita técnica',
  levantamiento: 'Levantamiento',
  prueba_piloto: 'Prueba piloto',
};

export const ETIQUETA_ESTADO_EVALUACION: Record<string, string> = {
  propuesta: 'Propuesta',
  aprobada: 'Aprobada',
  en_curso: 'En curso',
  exitosa: 'Exitosa',
  fallida: 'Fallida',
  cancelada: 'Cancelada',
};

export const ETIQUETA_TIPO_ACTIVIDAD: Record<string, string> = {
  reunion: 'Reunión',
  visita: 'Visita',
  demo: 'Demo',
  levantamiento: 'Levantamiento',
  diseno: 'Diseño',
  costeo: 'Costeo',
  propuesta: 'Propuesta',
  soporte_comercial: 'Soporte comercial',
  otro: 'Otro',
};

export const ETIQUETA_CATEGORIA_BRECHA: Record<string, string> = {
  producto: 'Producto',
  servicio: 'Servicio',
  certificacion: 'Certificación',
  capacidad: 'Capacidad',
  proveedor: 'Proveedor',
};

export const ETIQUETA_SEVERIDAD: Record<string, string> = { bloqueante: 'Bloqueante', alta: 'Alta', media: 'Media', baja: 'Baja' };
export const ETIQUETA_ESTADO_BRECHA: Record<string, string> = { abierta: 'Abierta', resuelta: 'Resuelta', aceptada: 'Aceptada' };

export const ETIQUETA_TIPO_SOLICITUD: Record<string, string> = {
  apoyo_tecnico: 'Apoyo técnico',
  visita: 'Visita',
  demo: 'Demo',
  diseno: 'Diseño',
  cotizacion_rapida: 'Cotización rápida',
  revision_pliego: 'Revisión de pliego',
};

export const ETIQUETA_ESTADO_SOLICITUD: Record<string, string> = {
  nueva: 'Nueva',
  asignada: 'Asignada',
  en_curso: 'En curso',
  entregada: 'Entregada',
  rechazada: 'Rechazada',
};

export const ETIQUETA_TIPO_BIBLIOTECA: Record<string, string> = {
  diagrama: 'Diagrama',
  propuesta_ganadora: 'Propuesta ganadora',
  solucion_referencia: 'Solución de referencia',
  ficha: 'Ficha técnica',
  demo: 'Demo',
  plantilla: 'Plantilla',
};

export const ETIQUETA_IMPACTO: Record<string, string> = { alto: 'Alto', medio: 'Medio', bajo: 'Bajo' };

export const ETIQUETA_VIABILIDAD: Record<string, string> = {
  viable: 'Viable',
  bajo_piso: 'Bajo el piso',
  sin_capacidad: 'Sin capacidad',
  no_aplica: 'No aplica',
  costo_invalido: 'Costo inválido',
  pendiente: 'Pendiente',
};
